import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFileText, FiArrowLeft, FiX, FiCheckCircle, FiClock } from 'react-icons/fi';
import PortalHeader from '../../components/Portal/PortalHeader';
import { usePortalInvoices } from '../../hooks/portal/usePortal';
import { getPortalInvoiceById } from '../../services/portal/portalApi';
import './PagarFactura.scss';

// Vista SOLO LECTURA de facturas del cliente en el portal:
// histórico completo, detalle e historial de pagos (registrados desde el
// admin). El cliente NO paga desde el portal.

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '—');

const PAYMENT_METHOD_LABELS = {
  cash: 'Efectivo',
  bank_transfer: 'Transferencia',
  credit_card: 'Tarjeta de crédito',
  debit_card: 'Tarjeta de débito',
  check: 'Cheque',
  electronic: 'Electrónico',
  other: 'Otro',
};

const statusBadge = (inv) => {
  const st = (inv.payment_status || '').toLowerCase();
  if (st === 'paid') return { label: 'Pagada', color: '#1e7e34', bg: '#e6f4ea' };
  if (st === 'overpaid') return { label: 'Sobrepagada', color: '#0b6b6b', bg: '#e0f5f5' };
  if (st === 'partial') return { label: 'Pago parcial', color: '#8a6d00', bg: '#fff3cd' };
  if (inv.is_overdue) return { label: 'Vencida', color: '#a11', bg: '#fde8e8' };
  return { label: 'Pendiente', color: '#7a4a00', bg: '#fdefd9' };
};

const PAGE_SIZE = 6;

const PagarFactura = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { invoices, summary, total, loading } = usePortalInvoices({
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  });
  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Si el total baja y la página actual queda fuera de rango, reajustar
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const openDetail = async (invoice) => {
    setSelected(invoice);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const detail = await getPortalInvoiceById(invoice.id);
      setSelected(detail);
    } catch (err) {
      setDetailError('No se pudo cargar el detalle de la factura');
      console.error('Error loading invoice detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
    setDetailError(null);
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="portal-page">
        <PortalHeader />
        <div className="portal-page__loading">Cargando facturas...</div>
      </div>
    );
  }

  const list = invoices || [];

  return (
    <div className="portal-page pagar-factura">
      <PortalHeader />

      <div className="portal-page__container">
        <button
          onClick={() => navigate('/portal')}
          className="back-button"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: 'transparent', border: 'none', color: '#7b2c7f',
            fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
            padding: '0.5rem 0', marginBottom: '1rem',
          }}
        >
          <FiArrowLeft size={20} /> Volver al Portal
        </button>

        <h1 className="portal-page__title">
          <FiFileText /> Mis Facturas
        </h1>

        {summary && (
          <div className="payment-summary">
            <div className="summary-card outstanding">
              <h3>Total Pendiente</h3>
              <p className="amount">{money(summary.total_outstanding)}</p>
            </div>
            <div className="summary-card overdue">
              <h3>Facturas Vencidas</h3>
              <p className="amount">{money(summary.overdue_amount)}</p>
            </div>
            <div className="summary-card" style={{ background: '#e6f4ea' }}>
              <h3>Pagado este mes</h3>
              <p className="amount">{money(summary.paid_this_month)}</p>
            </div>
          </div>
        )}

        <div className="invoices-section" style={{ marginTop: '1.5rem' }}>
          <h2 className="section-title">Histórico de Facturas</h2>

          {list.length > 0 ? (
            <div className="invoices-list">
              {list.map((invoice) => {
                const badge = statusBadge(invoice);
                return (
                  <div
                    key={invoice.id}
                    className="invoice-item"
                    onClick={() => openDetail(invoice)}
                    style={{ cursor: 'pointer' }}
                    title="Ver detalle"
                  >
                    <div className="invoice-header">
                      <span className="invoice-number">#{invoice.number}</span>
                      <span
                        className="invoice-status"
                        style={{
                          color: badge.color, background: badge.bg,
                          padding: '0.15rem 0.6rem', borderRadius: '999px',
                          fontSize: '0.8rem', fontWeight: 700,
                        }}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <div className="invoice-details">
                      <div className="detail-row">
                        <span className="label">Fecha:</span>
                        <span>{fmtDate(invoice.invoice_date)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Vencimiento:</span>
                        <span>{fmtDate(invoice.due_date)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Total:</span>
                        <span className="total-amount">{money(invoice.total)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Pagado:</span>
                        <span>{money(invoice.paid_amount)}</span>
                      </div>
                      {invoice.balance > 0 && (
                        <div className="detail-row">
                          <span className="label">Pendiente:</span>
                          <span className="balance-amount">{money(invoice.balance)}</span>
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: '0.5rem', color: '#7b2c7f', fontSize: '0.85rem', fontWeight: 600 }}>
                      Ver detalle y pagos →
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-invoices">
              <FiFileText />
              <p>Aún no tienes facturas registradas</p>
            </div>
          )}

          {total > 0 && (
            <div className="invoices-pagination">
              <button
                type="button"
                className="page-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
              >
                ← Anterior
              </button>
              <span className="page-info">
                Página {page} de {totalPages} · {total} factura{total === 1 ? '' : 's'}
              </span>
              <button
                type="button"
                className="page-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalle (solo lectura) */}
      {selected && (
        <div
          onClick={closeDetail}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem', zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: '12px', maxWidth: '640px',
              width: '100%', maxHeight: '90vh', overflowY: 'auto',
              padding: '1.5rem', boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, color: '#7b2c7f' }}>
                Factura #{selected.number}
              </h2>
              <button
                onClick={closeDetail}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}
                aria-label="Cerrar"
              >
                <FiX size={24} />
              </button>
            </div>

            {detailLoading ? (
              <p style={{ padding: '1rem 0' }}>Cargando detalle...</p>
            ) : detailError ? (
              <p style={{ color: '#a11', padding: '1rem 0' }}>{detailError}</p>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem', marginBottom: '1rem' }}>
                  <div><strong>Estado:</strong> {statusBadge(selected).label}</div>
                  <div><strong>Fecha:</strong> {fmtDate(selected.invoice_date)}</div>
                  <div><strong>Vencimiento:</strong> {fmtDate(selected.due_date)}</div>
                  {selected.paid_date && <div><strong>Pagada el:</strong> {fmtDate(selected.paid_date)}</div>}
                </div>

                {/* Items */}
                {Array.isArray(selected.items) && selected.items.length > 0 && (
                  <div style={{ marginBottom: '1rem', overflowX: 'auto' }}>
                    <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Detalle</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                          <th style={{ padding: '0.4rem' }}>Descripción</th>
                          <th style={{ padding: '0.4rem', textAlign: 'right' }}>Cant.</th>
                          <th style={{ padding: '0.4rem', textAlign: 'right' }}>P. Unit.</th>
                          <th style={{ padding: '0.4rem', textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.items.map((it) => (
                          <tr key={it.id} style={{ borderBottom: '1px solid #f2f2f2' }}>
                            <td style={{ padding: '0.4rem' }}>{it.description}</td>
                            <td style={{ padding: '0.4rem', textAlign: 'right' }}>{it.quantity}</td>
                            <td style={{ padding: '0.4rem', textAlign: 'right' }}>{money(it.unit_price)}</td>
                            <td style={{ padding: '0.4rem', textAlign: 'right' }}>{money(it.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totales */}
                <div style={{ borderTop: '1px solid #eee', paddingTop: '0.75rem', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Subtotal</span><span>{money(selected.subtotal)}</span>
                  </div>
                  {selected.discount_amount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Descuento</span><span>-{money(selected.discount_amount)}</span>
                    </div>
                  )}
                  {selected.shipping_cost > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Envío</span><span>{money(selected.shipping_cost)}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1.05rem', marginTop: '0.25rem' }}>
                    <span>Total</span><span>{money(selected.total)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#1e7e34' }}>
                    <span>Pagado</span><span>{money(selected.paid_amount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: selected.balance > 0 ? '#a11' : '#1e7e34' }}>
                    <span>Saldo pendiente</span><span>{money(selected.balance)}</span>
                  </div>
                </div>

                {/* Pagos (registrados desde el admin) */}
                <div>
                  <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Pagos realizados</h3>
                  {Array.isArray(selected.payments) && selected.payments.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {selected.payments.map((p, idx) => (
                        <div
                          key={p.id ?? `recon-${idx}`}
                          style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: '#f7f7f9', borderRadius: '8px', padding: '0.6rem 0.8rem',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <FiCheckCircle color="#1e7e34" />
                            <div>
                              <div style={{ fontWeight: 600 }}>{money(p.amount)}</div>
                              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                {p.reconstructed ? (
                                  `${fmtDate(p.payment_date)} · Pago aplicado`
                                ) : (
                                  `${fmtDate(p.payment_date)} · ${PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method || '—'}${p.reference_number ? ` · Ref: ${p.reference_number}` : ''}`
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {selected.payments_reconstructed && (
                        <div style={{ marginTop: '0.2rem', fontSize: '0.78rem', color: '#8a6d00' }}>
                          * El desglose por pago no está disponible para esta factura; se muestra el monto total aplicado.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666' }}>
                      <FiClock /> <span>Sin pagos registrados todavía.</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PagarFactura;
